import { BLOCKS } from "@contentful/rich-text-types";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Box, Container, Grid, Paper, Tooltip, Typography } from "@mui/material";
import { useContext, useRef } from "react";
import { ContentWrapper } from "../components/layout/ContentWrapper";
import { AnimatedView } from "../components/utils/AnimatedView";
import { BottomActionBar } from "../components/utils/BottomActionBar";
import { ExternalLink } from "../components/utils/ExternalLink";
import ReadingBar from "../components/utils/ReadingBar";
import RenderContent from "../components/utils/RenderContent";
import { SolutionFeatureCard } from "../components/view/SolutionFeatureCard";
import { routes } from "../constants/routes";
import { ContentfulContext } from "../context/ContentfulContext";
import { AboutUs, SolutionFeature } from "../models/schema";
import { colors } from "../theme/colors";
import { generateId } from "../utils/utilities";

const AboutUsPage = () => {
  const { content } = useContext(ContentfulContext);
  const aboutUs = content?.aboutUs as AboutUs;
  const {
    bottomBannerAction,
    bottomBannerLabel,
    certificatesCollection,
    certificatesLabel,
    historyDescription,
    historyLabel,
    solutionFeaturesLabel,
    solutionFeaturesCollection,
    marketingClaimsCollection,
    marketingClaimsLabel,
    missionDescription,
    missionLabel,
    title,
    valuesCollection,
    valuesLabel
  } = aboutUs;

  const certificatesRef = useRef<HTMLDivElement>(null);
  const scrollSize = 160;

  const scrollItems = (direction: "left" | "right") => {
    if (certificatesRef?.current) {
      certificatesRef.current.scrollLeft =
        direction === "left"
          ? certificatesRef.current.scrollLeft - scrollSize
          : certificatesRef.current.scrollLeft + scrollSize;
    }
  };

  return (
    <>
      <ReadingBar />
      <ContentWrapper title={title as string}>
        <Container sx={{ display: "flex", flexDirection: "column", gap: { xs: 5, md: 10 } }}>
          <Typography variant="h3" textAlign="center">
            {missionLabel}
          </Typography>
          <RenderContent
            content={missionDescription?.json}
            sx={{
              "& > :not(template) ~ :not(template)": {
                mt: 5
              }
            }}
          />

          {(valuesCollection?.items?.length || 0) > 0 && (
            <>
              <Typography variant="h3" textAlign="center">
                {valuesLabel}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                {valuesCollection?.items.map((value, index) => (
                  <AnimatedView
                    sx={{
                      boxSizing: "border-box",
                      width: { xs: "100%", md: "50%", lg: "33%" }
                    }}
                    key={generateId(5)}
                    animation="fadeIn"
                    duration={500}
                    delay={{
                      xs: 0,
                      md: index * 300
                    }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        gap: 5,
                        p: 5
                      }}>
                      <Box
                        component="img"
                        src={value?.icon?.url as string}
                        sx={{ width: "3rem" }}
                      />
                      <Typography variant="h4" textAlign="center">
                        {value?.title}
                      </Typography>
                      <RenderContent content={value?.description?.json} />
                    </Box>
                  </AnimatedView>
                ))}
              </Box>
            </>
          )}

          <Typography variant="h3" textAlign="center">
            {historyLabel}
          </Typography>
          <RenderContent
            content={historyDescription?.json}
            sx={{
              "& > :not(template) ~ :not(template)": {
                mt: 5
              }
            }}
          />
        </Container>
      </ContentWrapper>

      {(marketingClaimsCollection?.items?.length || 0) > 0 && (
        <Box
          sx={{
            width: "100%",
            backgroundColor: colors.grey5,
            p: 5,
            my: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}>
          <Container>
            <Typography variant="h3" textAlign="center" color={colors.white} fontWeight={100}>
              {marketingClaimsLabel}
            </Typography>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexWrap: "wrap",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "center"
              }}>
              {marketingClaimsCollection?.items.map((marketingClaim, index) => (
                <AnimatedView
                  sx={{
                    width: { xs: "100%", md: "33%" }
                  }}
                  key={generateId(5)}
                  animation="fadeIn"
                  duration={500}
                  delay={{
                    xs: 0,
                    md: index * 300
                  }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      textAlign: "center",
                      my: 5,
                      px: 5
                    }}>
                    <Typography
                      sx={{ fontSize: { xs: "2rem", md: "4rem" }, fontWeight: "100" }}
                      color={colors.yellow}>
                      {marketingClaim?.number}
                    </Typography>
                    <RenderContent
                      content={marketingClaim?.text?.json}
                      options={{
                        renderNode: {
                          [BLOCKS.PARAGRAPH]: (node, children) => (
                            <Typography
                              color={colors.white}
                              sx={{ fontSize: { xs: "1rem", md: "1.3rem" }, mb: 3 }}>
                              {children}
                            </Typography>
                          )
                        }
                      }}
                    />
                  </Box>
                </AnimatedView>
              ))}
            </Box>
          </Container>
        </Box>
      )}

      {(solutionFeaturesCollection?.items?.length || 0) > 0 && (
        <ContentWrapper>
          <Container sx={{ display: "flex", flexDirection: "column", gap: { xs: 5, md: 10 } }}>
            <Typography variant="h3" textAlign="center">
              {solutionFeaturesLabel}
            </Typography>

            <Grid container spacing={5}>
              {solutionFeaturesCollection?.items.map((solutionFeature) => (
                <Grid item xs={12} md={6} key={generateId(5)}>
                  <SolutionFeatureCard solutionFeature={solutionFeature as SolutionFeature} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </ContentWrapper>
      )}

      <ContentWrapper>
        <Container sx={{ display: "flex", flexDirection: "column", gap: { xs: 5, md: 10 } }}>
          {(certificatesCollection?.items?.length || 0) > 0 && (
            <>
              <Typography variant="h3" textAlign="center">
                {certificatesLabel}
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center"
                }}>
                <ArrowBackIosIcon
                  sx={{
                    display: { xs: "block", md: "none" },
                    position: "absolute",
                    left: "-1.3rem",
                    top: "35%",
                    color: colors.grey2
                  }}
                  onClick={() => scrollItems("left")}
                />
                <Box
                  ref={certificatesRef}
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexWrap: { xs: "nowrap", md: "wrap" },
                    overflow: "auto",
                    scrollBehavior: "smooth",
                    mb: 10,
                    justifyContent: { md: "center" }
                  }}>
                  {certificatesCollection?.items?.map((certificate, index) => (
                    <AnimatedView
                      key={generateId(5)}
                      animation="fadeIn"
                      duration={500}
                      delay={{
                        xs: 0,
                        md: index * 300
                      }}>
                      <ExternalLink
                        href={(certificate?.file?.url || certificate?.url) as string}
                        sx={{
                          display: "flex",
                          height: "100%"
                        }}>
                        <Tooltip title={certificate?.title} placement="top" arrow>
                          <Paper
                            elevation={3}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              textAlign: "center",
                              p: { xs: 2, md: 3 },
                              m: 3,
                              minHeight: { xs: "10rem", md: "12rem" },
                              width: { xs: "8rem", md: "10rem" },
                              flex: 1,
                              whiteSpace: "nowrap"
                            }}>
                            <Box
                              sx={{
                                flex: 1
                              }}
                            />
                            <Box
                              component="img"
                              src={certificate?.logo?.url as string}
                              sx={{
                                width: { xs: "6rem", md: "8rem" },
                                height: "auto",
                                mx: "auto"
                              }}
                            />
                            <Box
                              sx={{
                                flex: 1
                              }}
                            />
                            <Typography
                              textAlign="center"
                              sx={{
                                mt: 5,
                                textOverflow: "ellipsis",
                                overflow: "hidden"
                              }}>
                              {certificate?.title}
                            </Typography>
                          </Paper>
                        </Tooltip>
                      </ExternalLink>
                    </AnimatedView>
                  ))}
                </Box>
                <ArrowForwardIosIcon
                  sx={{
                    display: { xs: "block", md: "none" },
                    position: "absolute",
                    right: "-1.6rem",
                    top: "35%",
                    color: colors.grey2
                  }}
                  onClick={() => scrollItems("right")}
                />
              </Box>
            </>
          )}
        </Container>
      </ContentWrapper>

      <BottomActionBar
        actionLabel={bottomBannerAction as string}
        title={bottomBannerLabel as string}
        route={routes.solutions}
      />
    </>
  );
};

export default AboutUsPage;
